<template>
  <div class="relative mx-auto max-w-[var(--breakpoint-xl)] px-4 sm:px-8">
    <section class="w-full">
      <!-- Tabs for Locations -->
      <UTabs
        v-model="activeTab"
        :items="locationTabs"
        :ui="{
          root: '',
          list: 'bg-neutral-900/40 ring ring-yellow-400 backdrop-blur-lg max-w-xl z-[10] max-auto sticky top-28 lg:top-40',
          leadingIcon: 'hidden sm:block',
          trigger: 'disabled:!text-neutral-600 disabled:text-xs',
          label: 'font-bold',
          content: 'pt-12',
        }"
        @update:model-value="updateRouteParams"
      >
        <template #all>
          <div v-if="blok.enable_review_links" class="mb-8 max-w-prose">
            <h2 class="text-xl font-bold text-white">Leave A Review</h2>
            <p class="font-medium text-neutral-400">
              Select a location below to leave a review and claim your 15%
              discount code.
            </p>
          </div>
          <div
            class="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
          >
            <LazyMarketingCardLocation
              v-for="(location, index) in sortedLocations"
              v-if="!isLoading"
              :key="index"
              :location="location"
              :review-links="blok.enable_review_links"
            />
            <LazyMarketingCardLocationLoading
              v-for="i in 4"
              v-if="isLoading"
              :key="i"
              :review-links="blok.enable_review_links"
            />
          </div>
        </template>

        <template #activity>
          <div v-if="blok.enable_review_links" class="mb-8 max-w-prose">
            <h2 class="text-xl font-bold text-white">Leave A Review</h2>
            <p class="font-medium text-neutral-400">
              Select a location below to leave a review and claim your 15%
              discount code.
            </p>
          </div>
          <div
            class="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
          >
            <LazyMarketingCardLocation
              v-for="(location, index) in sortedLocations.filter(
                (location) => !location.name.toLowerCase().includes('climbing')
              )"
              v-if="!isLoading"
              :key="index"
              :location="location"
              :review-links="blok.enable_review_links"
            />
            <LazyMarketingCardLocationLoading
              v-for="i in 4"
              v-if="isLoading"
              :key="i"
              :review-links="blok.enable_review_links"
            />
          </div>
        </template>

        <template #climbing>
          <div v-if="blok.enable_review_links" class="mb-8 max-w-prose">
            <h2 class="text-xl font-bold text-white">Leave A Review</h2>
            <p class="font-medium text-neutral-400">
              Select a location below to leave a review and claim your 15%
              discount code.
            </p>
          </div>
          <div
            class="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
          >
            <LazyMarketingCardLocation
              v-for="(location, index) in sortedLocations.filter((location) =>
                location.name.toLowerCase().includes('climbing')
              )"
              v-if="!isLoading"
              :key="index"
              :location="location"
              :review-links="blok.enable_review_links"
            />
            <LazyMarketingCardLocationLoading
              v-for="i in 4"
              v-if="isLoading"
              :key="i"
              :review-links="blok.enable_review_links"
            />
          </div>
        </template>
      </UTabs>
    </section>
  </div>
</template>

<script setup lang="ts">
import type {
  ListLocationsStoryblok,
  LocationStoryblok,
} from '~/types/component-types-sb';
import type { ISbStoryData } from '@storyblok/vue';

interface Location extends ISbStoryData<LocationStoryblok> {
  activityCount: number;
}

const { fetchActivities, isLoading } = useStoryblokClient();
const route = useRoute();
const router = useRouter();

defineProps<{
  blok: ListLocationsStoryblok;
  uuid?: string;
  type?: string;
}>();

const activities = ref<any[]>([]);
const locations = ref<Location[]>([]);

// Items
const locationTabs = [
  {
    label: 'All',
    slot: 'all',
    disabled: false,
  },
  {
    label: 'Adventures',
    slot: 'activity',
    disabled: false,
  },
  {
    label: 'Climbing',
    slot: 'climbing',
    disabled: false,
  },
];

// Load activities and set unique locations with activity count
const loadActivities = async () => {
  const stories = await fetchActivities();

  // Filter out activities with activity_type 'corporate'
  activities.value = stories.filter(
    (activity) => activity.content?.activity_type !== 'corporates'
  );

  // Extract unique locations from filtered activities
  const locationMap = new Map<string, Location>();

  activities.value.forEach((activity) => {
    if (!activity.content?.locations) return;

    activity.content.locations.forEach(
      (loc: ISbStoryData<LocationStoryblok>) => {
        // Get the location name from the headline
        const locationName = loc.content?.headline || '';

        if (!locationName) {
          console.warn('Skipping location with no headline:', loc);
          return;
        }

        if (locationMap.has(locationName)) {
          const existing = locationMap.get(locationName);
          if (existing) {
            existing.activityCount = (existing.activityCount || 0) + 1;
          }
        } else {
          const newLocation = {
            ...loc,
            activityCount: 1,
            name: locationName,
            slug: loc.slug || '',
            content: {
              ...loc.content,
              name: locationName,
              town: loc.content?.town || '',
              review_count: loc.content?.review_count || '0',
              feature_image: loc.content?.feature_image || undefined,
            },
          };
          locationMap.set(locationName, newLocation);
        }
      }
    );
  });

  locations.value = Array.from(locationMap.values());
};

// Computed property to sort locations alphabetically
const sortedLocations = computed(() => {
  return [...locations.value].sort((a, b) => a.name.localeCompare(b.name));
});

await loadActivities();

const queryParam = route.query.tab;
const activeTab = ref('0');

// Set Active Tab
onMounted(() => {
  if (queryParam === 'activities') {
    activeTab.value = '1';
  } else if (queryParam === 'climbing') {
    activeTab.value = '2';
  }
});

// Update Query Params
const updateRouteParams = (newTabValue: string | number) => {
  let newTabQuery = '';

  if (newTabValue === '1') {
    newTabQuery = 'activities';
  } else if (newTabValue === '2') {
    newTabQuery = 'climbing';
  } else {
    newTabQuery = 'all';
  }

  // Update the route query parameter without reloading the page
  router.replace({
    query: {
      ...route.query, // Keep other query params
      tab: newTabQuery,
    },
  });

  // Scroll to the top of the section
  window.scrollTo({ top: 300, behavior: 'smooth' });
};

// Watch for changes in query parameters
watch(
  () => route.query.tab,
  (newTabQuery) => {
    // Perform any actions needed when the query parameter changes
    if (newTabQuery === 'activities') {
      activeTab.value = '1';
    } else if (newTabQuery === 'climbing') {
      activeTab.value = '2';
    }
  }
);
</script>
